import { useState, useEffect } from 'react';
import { usersApi } from '@/api/users';
import { companiesApi } from '@/api/companies';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Modal, ModalFooter } from '@/components/ui/modal';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/components/ui/toast';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { format } from 'date-fns';
import { Plus, Trash2 } from 'lucide-react';
import type { User, UserRole } from '@/types/user';
import type { Company } from '@/types/company';
import type { UserCreateInput, UserUpdateInput } from '@/api/users';

const roleOptions = [
  { value: 'super_admin', label: 'スーパー管理者' },
  { value: 'company_admin', label: '法人管理者' },
];

const roleLabels: Record<UserRole, string> = {
  super_admin: 'スーパー管理者',
  company_admin: '法人管理者',
};

export default function Users() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [createFormData, setCreateFormData] = useState<UserCreateInput>({
    email: '',
    password: '',
    full_name: '',
    role: 'company_admin',
    company_ids: [],
  });

  const [editFormData, setEditFormData] = useState<UserUpdateInput>({
    email: '',
    full_name: '',
    role: 'company_admin',
    is_active: true,
    company_ids: [],
  });

  const fetchUsers = async () => {
    try {
      const response = await usersApi.getUsers();
      setUsers(response.items);
    } catch (err) {
      console.error('ユーザー一覧取得エラー:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesApi.getCompanies();
      setCompanies(response.items);
    } catch (err) {
      console.error('法人一覧取得エラー:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  const resetCreateForm = () => {
    setCreateFormData({
      email: '',
      password: '',
      full_name: '',
      role: 'company_admin',
      company_ids: [],
    });
    setError(null);
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      await usersApi.createUser(createFormData);
      setIsCreateModalOpen(false);
      resetCreateForm();
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'ユーザーの作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setEditFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
      company_ids: user.company_ids || [],
    });
    setError(null);
    setIsEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingUser) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const updateData: UserUpdateInput = {};
      if (editFormData.email && editFormData.email !== editingUser.email) {
        updateData.email = editFormData.email;
      }
      if (editFormData.full_name) updateData.full_name = editFormData.full_name;
      if (editFormData.role) updateData.role = editFormData.role;
      if (editFormData.is_active !== undefined) updateData.is_active = editFormData.is_active;
      if (editFormData.company_ids !== undefined) updateData.company_ids = editFormData.company_ids;

      await usersApi.updateUser(editingUser.id, updateData);
      setIsEditModalOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'ユーザーの更新に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await usersApi.updateUser(user.id, {
        is_active: !user.is_active,
      });
      fetchUsers();
      showToast(user.is_active ? 'ユーザーを無効化しました' : 'ユーザーを有効化しました', 'success');
    } catch (err: any) {
      const message = err.response?.data?.detail || 'ユーザーの更新に失敗しました';
      showToast(message, 'warning');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('このユーザーを削除してもよろしいですか？')) return;

    try {
      await usersApi.deleteUser(id);
      fetchUsers();
    } catch (err) {
      console.error('ユーザー削除エラー:', err);
    }
  };

  const toggleCreateCompany = (companyId: number) => {
    const currentIds = createFormData.company_ids || [];
    if (currentIds.includes(companyId)) {
      setCreateFormData({
        ...createFormData,
        company_ids: currentIds.filter((id) => id !== companyId),
      });
    } else {
      setCreateFormData({
        ...createFormData,
        company_ids: [...currentIds, companyId],
      });
    }
  };

  const toggleEditCompany = (companyId: number) => {
    const currentIds = editFormData.company_ids || [];
    if (currentIds.includes(companyId)) {
      setEditFormData({
        ...editFormData,
        company_ids: currentIds.filter((id) => id !== companyId),
      });
    } else {
      setEditFormData({
        ...editFormData,
        company_ids: [...currentIds, companyId],
      });
    }
  };

  // モバイル用: ユーザーカード表示
  const UserCard = ({ user }: { user: User }) => (
    <div className="bg-white border rounded-lg p-4 space-y-3">
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 truncate">{user.full_name}</h3>
          <p className="text-sm text-gray-500 truncate">{user.email}</p>
        </div>
        <Badge variant={user.is_active ? 'success' : 'default'} className="shrink-0 ml-2">
          {user.is_active ? '有効' : '無効'}
        </Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        <Badge variant={user.role === 'super_admin' ? 'danger' : 'info'}>
          {roleLabels[user.role]}
        </Badge>
        <Badge variant="default">
          {user.auth_provider === 'google' ? 'Google' : 'メール'}
        </Badge>
      </div>

      <div className="text-sm">
        <span className="text-gray-500">最終ログイン: </span>
        <span className="text-gray-900">
          {user.last_login_at
            ? format(new Date(user.last_login_at), 'yyyy/MM/dd HH:mm')
            : '-'}
        </span>
      </div>

      <div className="flex items-center gap-3 pt-2 border-t">
        <Button variant="outline" size="sm" onClick={() => handleOpenEdit(user)}>
          編集
        </Button>
        <button
          onClick={() => handleToggleActive(user)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            user.is_active ? 'bg-green-500' : 'bg-gray-300'
          }`}
          title={user.is_active ? '無効にする' : '有効にする'}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              user.is_active ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
        <Button variant="danger" size="sm" onClick={() => handleDelete(user.id)} title="削除">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">ユーザー管理</h1>
          <p className="text-sm sm:text-base text-gray-500">システムユーザーを管理します</p>
        </div>
        <Button onClick={() => { resetCreateForm(); setIsCreateModalOpen(true); }} className="w-full sm:w-auto">
          <Plus className="w-4 h-4 mr-1" />
          新規ユーザー登録
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>ユーザー一覧</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              登録されているユーザーがいません
            </div>
          ) : (
            <>
              {/* デスクトップ: テーブル表示 */}
              <div className="hidden lg:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>名前</TableHead>
                      <TableHead>メール</TableHead>
                      <TableHead>権限</TableHead>
                      <TableHead>認証方法</TableHead>
                      <TableHead>ステータス</TableHead>
                      <TableHead>最終ログイン</TableHead>
                      <TableHead>操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{user.email}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'super_admin' ? 'danger' : 'info'}>
                            {roleLabels[user.role]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="default">
                            {user.auth_provider === 'google' ? 'Google' : 'メール'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={user.is_active ? 'success' : 'default'}>
                            {user.is_active ? '有効' : '無効'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.last_login_at
                            ? format(new Date(user.last_login_at), 'yyyy/MM/dd HH:mm')
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2 whitespace-nowrap items-center">
                            <Button variant="outline" size="sm" onClick={() => handleOpenEdit(user)}>
                              編集
                            </Button>
                            <button
                              onClick={() => handleToggleActive(user)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                user.is_active ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                              title={user.is_active ? '無効にする' : '有効にする'}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  user.is_active ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <Button variant="danger" size="sm" onClick={() => handleDelete(user.id)} title="削除">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* モバイル/タブレット: カード表示 */}
              <div className="lg:hidden space-y-4">
                {users.map((user) => (
                  <UserCard key={user.id} user={user} />
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* 新規ユーザー登録モーダル */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="新規ユーザー登録"
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="氏名"
            value={createFormData.full_name}
            onChange={(e) => setCreateFormData({ ...createFormData, full_name: e.target.value })}
            placeholder="山田太郎"
            required
          />
          <Input
            label="メールアドレス"
            type="email"
            value={createFormData.email}
            onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
            placeholder="user@example.com"
            required
          />
          <Input
            label="パスワード"
            type="password"
            value={createFormData.password}
            onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
            placeholder="8文字以上"
            required
          />
          <Select
            label="権限"
            options={roleOptions}
            value={createFormData.role}
            onChange={(value) => setCreateFormData({ ...createFormData, role: value as UserRole })}
          />

          {createFormData.role === 'company_admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                担当法人
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                {companies.map((company) => (
                  <Checkbox
                    key={company.id}
                    label={company.display_name}
                    checked={createFormData.company_ids?.includes(company.id) || false}
                    onChange={() => toggleCreateCompany(company.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
            キャンセル
          </Button>
          <Button onClick={handleCreate} isLoading={isSubmitting}>
            登録
          </Button>
        </ModalFooter>
      </Modal>

      {/* ユーザー編集モーダル */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditingUser(null); }}
        title={`ユーザー編集: ${editingUser?.full_name || ''}`}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="氏名"
            value={editFormData.full_name || ''}
            onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })}
            placeholder="山田太郎"
            required
          />
          <Input
            label="メールアドレス"
            type="email"
            value={editFormData.email || ''}
            onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            placeholder="user@example.com"
            required
          />
          <div className="p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-500">認証方法</p>
            <p className="font-medium">{editingUser?.auth_provider === 'google' ? 'Google' : 'メール'}</p>
          </div>
          <Select
            label="権限"
            options={roleOptions}
            value={editFormData.role || 'company_admin'}
            onChange={(value) => setEditFormData({ ...editFormData, role: value as UserRole })}
          />

          {editFormData.role === 'company_admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                担当法人
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                {companies.map((company) => (
                  <Checkbox
                    key={company.id}
                    label={company.display_name}
                    checked={editFormData.company_ids?.includes(company.id) || false}
                    onChange={() => toggleEditCompany(company.id)}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Checkbox
              label="有効"
              checked={editFormData.is_active || false}
              onChange={() => setEditFormData({ ...editFormData, is_active: !editFormData.is_active })}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={() => { setIsEditModalOpen(false); setEditingUser(null); }}>
            キャンセル
          </Button>
          <Button onClick={handleUpdate} isLoading={isSubmitting}>
            更新
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}
